import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, KanbanSquare, ArrowUpDown, ChevronDown, Plus, Archive, CheckCircle2, LayoutGrid, List, Infinity as InfinityIcon, Layers, ChevronRight } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { iniciales, tiempoRelativo } from './utils/helpers';
import ProyectosListView from './ProyectosListView';
import { VISTA_GENERAL_SLUG } from './VistaGeneralView';
import EmptyState from './EmptyState';
import { BUTTON } from './utils/uiTokens';

// Helper de porcentaje completado para sort por progreso.
const pct = (p) => {
  const t = p.stats?.total ?? 0;
  const h = p.stats?.hechas ?? 0;
  return t > 0 ? h / t : 0;
};

// Keys direccionales `<columna>_<asc|desc>`. Compartidas entre el dropdown del
// toolbar (vista grid) y los headers sortables (vista lista). La lista lee la
// columna activa desde el prefijo, así que los nombres importan.
const ORDENES = {
  actividad_desc: { label: 'Recientes',       fn: (a, b) => new Date(b.updated_at) - new Date(a.updated_at) },
  actividad_asc:  { label: 'Antiguos',        fn: (a, b) => new Date(a.updated_at) - new Date(b.updated_at) },
  nombre_asc:     { label: 'Alfabético A-Z',  fn: (a, b) => a.nombre.localeCompare(b.nombre) },
  nombre_desc:    { label: 'Alfabético Z-A',  fn: (a, b) => b.nombre.localeCompare(a.nombre) },
  progreso_asc:   { label: 'Más pendientes',  fn: (a, b) => pct(a) - pct(b) },
  progreso_desc:  { label: 'Más avance',      fn: (a, b) => pct(b) - pct(a) },
};

// Agrupacion del dropdown: par desc/asc por columna (cada par muestra
// dos pills tipo "Z→A" / "A→Z" para invertir en un click). Mas legible que
// listar 6 lineas planas, y deja explícita la dirección.
const ORDENES_GRUPOS = [
  { label: 'Actividad',   desc: 'actividad_desc', asc: 'actividad_asc',  descLabel: 'Recientes',     ascLabel: 'Antiguos' },
  { label: 'Alfabético',  desc: 'nombre_desc',    asc: 'nombre_asc',     descLabel: 'Z → A',         ascLabel: 'A → Z' },
  { label: 'Progreso',    desc: 'progreso_desc',  asc: 'progreso_asc',   descLabel: 'Más avance',    ascLabel: 'Más pendientes' },
];

// v2: se cambio el default de 'grid' a 'lista'; la clave nueva hace que la
// preferencia vieja no siga imponiendo la grilla.
const VISTA_KEY = 'proyectos-home-vista-v2'; // 'grid' | 'lista'

// ---------- Acceso a la vista general ----------

// Renglon por encima de la tabla: mismo peso visual que una fila de proyecto,
// pero lleva al tablero agregado (todas las tareas de todos los proyectos).
const VistaGeneralRow = ({ proyectos }) => {
  const activas = proyectos.reduce(
    (sum, p) => sum + (p.stats?.activas ?? Math.max((p.stats?.total ?? 0) - (p.stats?.hechas ?? 0), 0)),
    0
  );

  return (
    <Link
      to={`/proyectos/${VISTA_GENERAL_SLUG}`}
      className="group flex items-center gap-3 mb-3 px-4 py-3 bg-white rounded-xl border border-slate-200/70 hover:border-cyan-300 hover:shadow-[0_6px_20px_-14px_rgba(15,23,42,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/30 transition-all"
    >
      <span className="w-7 h-7 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center flex-shrink-0">
        <Layers size={14} strokeWidth={2} />
      </span>
      <span className="text-sm font-medium text-slate-900">Vista general</span>
      <span className="hidden sm:inline text-xs text-slate-400 truncate">
        Las tareas de todos tus proyectos en un solo tablero
      </span>
      <span className="flex-1" />
      <span className="text-[11px] text-slate-400 tabular-nums whitespace-nowrap">
        {activas} {activas === 1 ? 'tarea activa' : 'tareas activas'}
      </span>
      <ChevronRight
        size={14}
        className="text-slate-300 group-hover:text-cyan-500 group-hover:translate-x-0.5 transition-all flex-shrink-0"
      />
    </Link>
  );
};

// ---------- Tarjeta de proyecto ----------

const ProjectCard = ({ proyecto, index, onToggleFavorito }) => {
  const color = proyecto.color || '#06b6d4';
  const total = proyecto.stats?.total ?? 0;
  const hechas = proyecto.stats?.hechas ?? 0;
  const activas = proyecto.stats?.activas ?? Math.max(total - hechas, 0);
  const progreso = total > 0 ? Math.round((hechas / total) * 100) : 0;
  const esContinuo = !!proyecto.es_continuo;
  const miembros = (proyecto.miembros || []).slice(0, 4);
  const extra = (proyecto.miembros?.length || 0) - miembros.length;
  // Cap del stagger: con muchas cards el último no entra demasiado tarde.
  const delay = Math.min(index * 0.04, 0.3);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to={`/proyectos/${proyecto.slug}`}
        className="group block bg-white rounded-xl border border-slate-200/70 overflow-hidden transition-all duration-200 hover:border-[var(--proj-color)] hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.12)] hover:-translate-y-px"
        style={{ '--proj-color': color }}
      >
        <div className="p-5">
          {/* Header: slug + favorito */}
          <div className="flex items-start justify-between mb-2">
            <span className="font-mono text-[10px] tracking-widest text-slate-400 uppercase">
              {proyecto.slug}
            </span>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleFavorito(proyecto);
              }}
              className="p-1 -m-1 rounded text-slate-300 hover:text-amber-400 transition-colors active:scale-90"
              title={proyecto.es_favorito ? 'Quitar de favoritos' : 'Marcar como favorito'}
            >
              <Star
                size={15}
                strokeWidth={2}
                className={proyecto.es_favorito ? 'fill-amber-400 text-amber-400' : ''}
              />
            </button>
          </div>

          {/* Nombre */}
          <h3 className="text-base font-semibold text-slate-900 tracking-tight leading-snug mb-1 line-clamp-2">
            {proyecto.nombre}
          </h3>

          {/* Descripcion: 4 líneas reservadas, respeta saltos del original.
              leading-4 (16px) × 4 líneas = h-16 (64px) exacto, sin cortes a media letra. */}
          <p className="text-xs text-slate-500 mb-4 h-16 leading-4 whitespace-pre-line overflow-hidden">
            {proyecto.descripcion}
          </p>

          {/* Progreso / continuo: para proyectos sin fecha de fin, la barra no aplica —
              mostramos chip "Continuo" + contador de tareas activas. */}
          {esContinuo ? (
            <div className="mb-4 flex items-center gap-2 h-6">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wider bg-cyan-50 text-cyan-700">
                <InfinityIcon size={11} strokeWidth={2.5} />
                Continuo
              </span>
              <span className="text-[11px] text-slate-500 tabular-nums">
                {activas} {activas === 1 ? 'activa' : 'activas'}
              </span>
            </div>
          ) : (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] text-slate-500 tabular-nums">
                  {hechas} de {total} {total === 1 ? 'tarea' : 'tareas'}
                </span>
                <span className="text-[11px] font-medium text-slate-700 tabular-nums">
                  {progreso}%
                </span>
              </div>
              <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ backgroundColor: color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${progreso}%` }}
                  transition={{ duration: 0.6, delay: 0.1 + delay, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
            </div>
          )}

          {/* Footer: miembros + tiempo */}
          <div className="flex items-center justify-between">
            <div className="flex items-center -space-x-1.5">
              {miembros.map((m) => (
                <div
                  key={m.empleado_id}
                  title={m.nombre}
                  className="w-6 h-6 rounded-full ring-2 ring-white text-[10px] font-semibold flex items-center justify-center"
                  style={{ background: `linear-gradient(${color}20, ${color}20), #fff`, color }}
                >
                  {iniciales(m.nombre)}
                </div>
              ))}
              {extra > 0 && (
                <div className="w-6 h-6 rounded-full ring-2 ring-white bg-slate-50 text-slate-500 text-[10px] font-medium flex items-center justify-center">
                  +{extra}
                </div>
              )}
            </div>

            <span className="text-[11px] text-slate-400">
              {tiempoRelativo(proyecto.updated_at)}
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

// ---------- Empty state ----------

const ProyectosEmpty = ({ filtro, onCrear, onVolverActivos }) => {
  const labels = {
    activos: { titulo: 'Aún no perteneces a ningún proyecto', sub: 'Crea uno para empezar a organizar el trabajo de tu equipo.' },
    archivados: { titulo: 'No tienes proyectos archivados', sub: 'Los proyectos archivados aparecerán aquí.' },
    favoritos: { titulo: 'Aún no tienes favoritos', sub: 'Marca con estrella los proyectos que quieras tener a mano.' },
  };
  const l = labels[filtro] || labels.activos;

  return (
    <div className="py-24 px-6">
      <EmptyState
        icon={KanbanSquare}
        title={l.titulo}
        description={l.sub}
        variant="centered"
        action={
          filtro === 'activos' ? (
            <button onClick={onCrear} className={`${BUTTON.primary} ${BUTTON.size.md}`}>
              <Plus size={14} strokeWidth={2.5} />
              Crear tu primer proyecto
            </button>
          ) : (
            <button
              onClick={onVolverActivos}
              className={`${BUTTON.secondary} ${BUTTON.size.md}`}
            >
              Ver proyectos activos
            </button>
          )
        }
      />
    </div>
  );
};

// ---------- Vista principal ----------

const ProyectosHomeView = () => {
  const { searchTerm = '', openNewProject } = useOutletContext() || {};
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('activos'); // activos | archivados | favoritos
  const [orden, setOrden] = useState('actividad_desc');
  const [showOrdenDropdown, setShowOrdenDropdown] = useState(false);
  const ordenRef = useRef(null);
  const [vista, setVista] = useState(() => {
    if (typeof window === 'undefined') return 'lista';
    const v = window.localStorage.getItem(VISTA_KEY);
    return v === 'grid' ? 'grid' : 'lista';
  });

  useEffect(() => {
    try { window.localStorage.setItem(VISTA_KEY, vista); } catch (_) {}
  }, [vista]);

  // Click-outside para cerrar el dropdown del sort (reemplaza el patron
  // onBlur+setTimeout que perdía clicks con teclado y scroll).
  useEffect(() => {
    if (!showOrdenDropdown) return;
    const onClick = (e) => {
      if (ordenRef.current && !ordenRef.current.contains(e.target)) setShowOrdenDropdown(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showOrdenDropdown]);

  const fetchProyectos = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filtro === 'archivados') params.archivados = true;
      if (filtro === 'favoritos') params.favoritos = true;
      const { data } = await axiosInstance.get('/api/proyectos', { params });
      setProyectos(data || []);
    } catch (err) {
      console.error('Error cargando proyectos:', err);
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  useEffect(() => {
    fetchProyectos();
  }, [fetchProyectos]);

  const handleToggleFavorito = async (proyecto) => {
    try {
      const { data } = await axiosInstance.post(`/api/proyectos/${proyecto.id}/favorito`);
      setProyectos((prev) => prev.map((p) =>
        p.id === proyecto.id ? { ...p, es_favorito: data.favorito } : p
      ));
    } catch (err) {
      console.error('Error toggle favorito:', err);
    }
  };

  const proyectosFiltrados = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let lista = q
      ? proyectos.filter((p) =>
          p.nombre.toLowerCase().includes(q)
          || p.slug.toLowerCase().includes(q)
          || (p.descripcion || '').toLowerCase().includes(q)
        )
      : proyectos;
    const fn = (ORDENES[orden] || ORDENES.actividad_desc).fn;
    return [...lista].sort(fn);
  }, [proyectos, searchTerm, orden]);

  const favoritos = proyectosFiltrados.filter((p) => p.es_favorito);
  const otros = proyectosFiltrados.filter((p) => !p.es_favorito);

  // Contador contextual: refleja el filtro activo en lugar de un genérico "proyectos".
  const totalLabel = (() => {
    const n = proyectosFiltrados.length;
    if (filtro === 'favoritos')  return `${n} ${n === 1 ? 'favorito' : 'favoritos'}`;
    if (filtro === 'archivados') return `${n} ${n === 1 ? 'archivado' : 'archivados'}`;
    return `${n} ${n === 1 ? 'proyecto' : 'proyectos'}`;
  })();

  const filtroTabBase = "px-3 py-1.5 text-sm font-medium rounded-md transition-all";
  const filtroTabIdle = "text-slate-500 hover:text-slate-900 hover:bg-slate-100";
  const filtroTabActive = "text-slate-900 bg-white shadow-sm ring-1 ring-slate-200/70";

  return (
    <div className="max-w-[1400px] mx-auto px-8 pt-8 pb-12">
      {/* Toolbar: filtros a la izquierda, total + sort a la derecha */}
      <div className="flex items-center justify-between mb-6">
        <div className="inline-flex items-center gap-0.5 p-0.5 bg-slate-100/80 rounded-lg">
          <button
            onClick={() => setFiltro('activos')}
            className={`${filtroTabBase} ${filtro === 'activos' ? filtroTabActive : filtroTabIdle}`}
          >
            <CheckCircle2 size={14} className="inline mr-1.5 -mt-0.5" />
            Activos
          </button>
          <button
            onClick={() => setFiltro('favoritos')}
            className={`${filtroTabBase} ${filtro === 'favoritos' ? filtroTabActive : filtroTabIdle}`}
          >
            <Star size={14} className="inline mr-1.5 -mt-0.5" />
            Favoritos
          </button>
          <button
            onClick={() => setFiltro('archivados')}
            className={`${filtroTabBase} ${filtro === 'archivados' ? filtroTabActive : filtroTabIdle}`}
          >
            <Archive size={14} className="inline mr-1.5 -mt-0.5" />
            Archivados
          </button>
        </div>

        {/* Total + guardar + toggle + sort dropdown */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-400 tabular-nums">
            {totalLabel}
          </span>
          <div className="w-px h-4 bg-slate-200" />

          {/* Toggle grid/lista — calza con los filter tabs (rounded-lg outer, rounded-md inner). */}
          <div className="inline-flex items-center gap-0.5 p-0.5 bg-slate-100/80 rounded-lg">
            <button
              onClick={() => setVista('grid')}
              className={`p-1.5 rounded-md transition-colors ${
                vista === 'grid'
                  ? 'bg-white shadow-sm ring-1 ring-slate-200/70 text-slate-700'
                  : 'text-slate-400 hover:text-slate-700'
              }`}
              title="Vista grilla"
            >
              <LayoutGrid size={14} strokeWidth={2} />
            </button>
            <button
              onClick={() => setVista('lista')}
              className={`p-1.5 rounded-md transition-colors ${
                vista === 'lista'
                  ? 'bg-white shadow-sm ring-1 ring-slate-200/70 text-slate-700'
                  : 'text-slate-400 hover:text-slate-700'
              }`}
              title="Vista lista"
            >
              <List size={14} strokeWidth={2} />
            </button>
          </div>

          {/* Sort dropdown: solo en vista grid (en lista los headers reemplazan al control). */}
          {vista === 'grid' && (
            <div ref={ordenRef} className="relative">
              <button
                onClick={() => setShowOrdenDropdown((v) => !v)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
              >
                <ArrowUpDown size={13} />
                <span>{ORDENES[orden]?.label || 'Ordenar'}</span>
                <ChevronDown
                  size={11}
                  className={`text-slate-300 transition-transform ${showOrdenDropdown ? 'rotate-180' : ''}`}
                />
              </button>
              {showOrdenDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -4, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.12 }}
                  className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-xl ring-1 ring-slate-200 py-1 z-10"
                >
                  {ORDENES_GRUPOS.map((g, idx) => (
                    <div key={g.label} className={idx > 0 ? 'border-t border-slate-100 mt-0.5 pt-0.5' : ''}>
                      <div className="px-3 pt-1 pb-0.5 text-[10px] font-semibold tracking-widest uppercase text-slate-400">
                        {g.label}
                      </div>
                      {[
                        { key: g.desc, label: g.descLabel },
                        { key: g.asc, label: g.ascLabel },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          onMouseDown={() => { setOrden(opt.key); setShowOrdenDropdown(false); }}
                          className={`block w-full text-left px-3 py-1.5 text-sm transition-colors ${
                            orden === opt.key ? 'bg-slate-50 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  ))}
                </motion.div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Acceso a la vista general. Solo con el filtro "Activos" y sin busqueda:
          en favoritos/archivados o filtrando por texto seria ruido. */}
      {!loading && filtro === 'activos' && !searchTerm.trim() && proyectos.length > 0 && (
        <VistaGeneralRow proyectos={proyectos} />
      )}

      {/* Contenido */}
      {vista === 'lista' ? (
        loading || proyectosFiltrados.length > 0 ? (
          <ProyectosListView
            proyectos={proyectosFiltrados}
            orden={orden}
            setOrden={setOrden}
            onToggleFavorito={handleToggleFavorito}
            loading={loading}
            filtro={filtro}
          />
        ) : (
          <ProyectosEmpty
            filtro={filtro}
            onCrear={openNewProject}
            onVolverActivos={() => setFiltro('activos')}
          />
        )
      ) : loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200/70 p-5 skeleton">
              <div className="h-3 w-12 bg-slate-100 rounded mb-3" />
              <div className="h-4 w-3/4 bg-slate-100 rounded mb-2" />
              <div className="h-3 w-1/2 bg-slate-100 rounded mb-5" />
              <div className="h-1 w-full bg-slate-100 rounded mb-5" />
              <div className="flex justify-between">
                <div className="h-6 w-16 bg-slate-100 rounded-full" />
                <div className="h-3 w-12 bg-slate-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : proyectosFiltrados.length === 0 ? (
        <ProyectosEmpty
          filtro={filtro}
          onCrear={openNewProject}
          onVolverActivos={() => setFiltro('activos')}
        />
      ) : (
        <>
          {favoritos.length > 0 && (
            <section className="mb-10">
              <div className="flex items-center gap-2 mb-3">
                <Star size={12} className="text-amber-400 fill-amber-400" />
                <h2 className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">
                  Favoritos
                </h2>
                <div className="flex-1 h-px bg-slate-200/60" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {favoritos.map((p, i) => (
                  <ProjectCard
                    key={p.id}
                    proyecto={p}
                    index={i}
                    onToggleFavorito={handleToggleFavorito}
                  />
                ))}
              </div>
            </section>
          )}

          {otros.length > 0 && (
            <section>
              {favoritos.length > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">
                    Todos los proyectos
                  </h2>
                  <div className="flex-1 h-px bg-slate-200/60" />
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {otros.map((p, i) => (
                  <ProjectCard
                    key={p.id}
                    proyecto={p}
                    index={i}
                    onToggleFavorito={handleToggleFavorito}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default ProyectosHomeView;
